import { MAX_BATCH, type ActivityEvent, type EventType, type FairState } from '@jobfair/shared';
import { KEYS, session } from './storage';

export interface TrackContext {
  phase?: FairState;
  dataVersion?: string;
}

export interface TrackerOptions {
  /** Ingest endpoint. Empty disables tracking entirely. */
  url: string;
  /** Server-corrected clock. */
  now: () => number;
  context: () => TrackContext;
  /** Return true when the payload was handed off. Injected for tests. */
  send?: (url: string, body: string) => boolean;
  flushMs?: number;
  /** Events beyond this are dropped oldest-first. Analytics must never grow memory unbounded. */
  maxQueue?: number;
  sid?: string;
}

export interface Tracker {
  track(type: EventType, extra?: { unitId?: string; value?: string }): void;
  flush(): void;
  stop(): void;
}

/** Random per-tab id. Not a cookie, not tied to a person, gone when the tab closes. */
export function sessionId(): string {
  const existing = session.get(KEYS.sid);
  if (existing) return existing;
  const bytes = new Uint8Array(12);
  (
    globalThis.crypto ?? { getRandomValues: (a: Uint8Array) => a.map(() => Math.random() * 256) }
  ).getRandomValues(bytes);
  const id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  session.set(KEYS.sid, id);
  return id;
}

/** sendBeacon survives page unload. text/plain avoids a CORS preflight; the server parses the body as JSON. */
function defaultSend(url: string, body: string): boolean {
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      return navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
    }
    void fetch(url, {
      method: 'POST',
      body,
      keepalive: true,
      headers: { 'content-type': 'text/plain' },
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

/**
 * Batches anonymous activity events. Fire-and-forget: nothing here ever blocks the UI or throws,
 * and losing a batch is acceptable by design.
 */
export function createTracker(opt: TrackerOptions): Tracker {
  const noop: Tracker = { track() {}, flush() {}, stop() {} };
  if (!opt.url) return noop;

  const send = opt.send ?? defaultSend;
  const maxQueue = opt.maxQueue ?? 500;
  const sid = opt.sid ?? sessionId();
  let queue: ActivityEvent[] = [];
  // Jitter so a thousand tabs don't flush in the same second.
  const period = (opt.flushMs ?? 10_000) * (0.8 + Math.random() * 0.4);
  let timer: ReturnType<typeof setInterval> | undefined;

  const flush = () => {
    while (queue.length) {
      const batch = queue.slice(0, MAX_BATCH);
      queue = queue.slice(MAX_BATCH);
      if (!send(opt.url, JSON.stringify({ events: batch }))) break;
    }
  };

  const onHide = () => {
    if (document.visibilityState === 'hidden') flush();
  };

  if (typeof document !== 'undefined') {
    timer = setInterval(flush, period);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
  }

  return {
    track(type, extra) {
      try {
        const ctx = opt.context();
        const ev: ActivityEvent = {
          type,
          ts: opt.now(),
          sid,
          ...(ctx.phase ? { phase: ctx.phase } : {}),
          ...(ctx.dataVersion ? { dataVersion: ctx.dataVersion } : {}),
          ...(extra?.unitId ? { unitId: extra.unitId.slice(0, 64) } : {}),
          ...(extra?.value ? { value: extra.value.slice(0, 200) } : {}),
        };
        queue.push(ev);
        if (queue.length > maxQueue) queue = queue.slice(queue.length - maxQueue);
        if (queue.length >= MAX_BATCH) flush();
      } catch {
        /* tracking must never break the page */
      }
    },
    flush,
    stop() {
      if (timer) clearInterval(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onHide);
        window.removeEventListener('pagehide', flush);
      }
      flush();
    },
  };
}
